import React from 'react';
import { useLocation } from 'react-router-dom';
import type { LocalBusiness, WithContext } from 'schema-dts';

interface LocalBusinessSchemaProps {
  customData?: Partial<LocalBusiness>;
}

export function LocalBusinessSchema({ customData = {} }: LocalBusinessSchemaProps) {
  const location = useLocation();

  const baseBusinessData: WithContext<LocalBusiness> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://xxlservices.com",
    "name": "XXL IT Services",
    "alternateName": "XXL IT Technology Services",
    "description": "Commercial low voltage contractor and IT company in Meridian, Idaho, serving Boise, Nampa, Eagle and the Treasure Valley: structured cabling and fiber, business network installation, Wi-Fi, security cameras, access control (UniFi Access and PDK) and managed IT. Certified UniFi installers.",
    "url": "https://xxlservices.com",
    "logo": "https://xxlservices.com/logo.svg",
    "image": [
      "https://xxlservices.com/logo.svg",
      "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=2000"
    ],
    "telephone": "+1-208-328-4500",
    "email": "info@xxlservices.com",
    "foundingDate": "2005-04-07",
    "priceRange": "$$",
    "currenciesAccepted": "USD",
    "paymentAccepted": ["Credit Card", "Debit Card", "Check", "Cash"],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1624 E Territory Dr",
      "addressLocality": "Meridian",
      "addressRegion": "ID",
      "postalCode": "83646",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 43.6468748,
      "longitude": -116.3740863
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "07:00",
        "closes": "20:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday", "Sunday"],
        "opens": "09:00",
        "closes": "19:00"
      }
    ],
    "areaServed": [
      {
        "@type": "City",
        "name": "Meridian",
        "containedInPlace": {
          "@type": "State",
          "name": "Idaho"
        }
      },
      {
        "@type": "City",
        "name": "Boise",
        "containedInPlace": {
          "@type": "State",
          "name": "Idaho"
        }
      },
      {
        "@type": "City",
        "name": "Nampa",
        "containedInPlace": {
          "@type": "State",
          "name": "Idaho"
        }
      },
      {
        "@type": "City",
        "name": "Eagle",
        "containedInPlace": {
          "@type": "State",
          "name": "Idaho"
        }
      },
      {
        "@type": "City",
        "name": "Kuna",
        "containedInPlace": {
          "@type": "State",
          "name": "Idaho"
        }
      },
      {
        "@type": "City",
        "name": "Star",
        "containedInPlace": {
          "@type": "State",
          "name": "Idaho"
        }
      },
      {
        "@type": "City",
        "name": "Garden City",
        "containedInPlace": {
          "@type": "State",
          "name": "Idaho"
        }
      },
      {
        "@type": "City",
        "name": "Caldwell",
        "containedInPlace": {
          "@type": "State",
          "name": "Idaho"
        }
      }
    ],
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 43.6468748,
        "longitude": -116.3740863
      },
      "geoRadius": "50000"
    },
    "sameAs": [
      "https://facebook.com/XXLIT",
      "https://www.linkedin.com/company/95933739/",
      "https://g.co/kgs/w7e81en"
    ],
    "hasMap": "https://www.google.com/maps/place/1624+E+Territory+Dr,+Meridian,+ID+83646",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Commercial Low Voltage and IT Services",
      "itemListElement": [
        ["Structured Cabling and Fiber Installation", "Cat6 and Cat6a drops, fiber backbone runs, racks and patch panels installed by our own crew", "/services/structured-cabling"],
        ["Business Network Installation", "Cabling, racks, managed switches, firewalls and Wi-Fi from one team", "/services/network"],
        ["Security Camera Installation", "UniFi Protect IP camera systems for offices, warehouses and stores", "/services/security-cameras"],
        ["Access Control Installation", "Key card, fob and phone entry with UniFi Access and PDK, including electric door hardware", "/services/access-control"],
        ["UniFi Installation", "Certified UniFi installers for network, Wi-Fi, cameras and door access", "/services/unifi-installation"],
        ["Managed IT Services", "IT support and management for Treasure Valley businesses", "/services/it-support"]
      ].map(([name, description, path]) => ({
        "@type": "Offer" as const,
        "itemOffered": {
          "@type": "Service" as const,
          "name": name,
          "description": description,
          "url": `https://xxlservices.com${path}`,
          "areaServed": ["Boise, ID", "Meridian, ID", "Nampa, ID", "Eagle, ID"]
        }
      }))
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+1-208-328-4500",
        "contactType": "customer service",
        "availableLanguage": "English",
        "hoursAvailable": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "07:00",
            "closes": "20:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Saturday", "Sunday"],
            "opens": "09:00",
            "closes": "19:00"
          }
        ]
      },
      {
        "@type": "ContactPoint",
        "telephone": "+1-208-328-4500",
        "contactType": "emergency",
        "availableLanguage": "English",
        "hoursAvailable": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "00:00",
          "closes": "23:59"
        }
      }
    ]
  };

  // Merge with custom data for specific pages
  const schemaData = { ...baseBusinessData, ...customData };

  React.useEffect(() => {
    try {
      // Remove existing schema markup
      const existingSchema = document.querySelector('#local-business-schema');
      if (existingSchema) {
        existingSchema.remove();
      }

      // Create and inject new schema markup
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'local-business-schema';
      script.textContent = JSON.stringify(schemaData, null, 2);
      document.head.appendChild(script);
    } catch (error) {
      console.error('LocalBusinessSchema: Error injecting schema into DOM', error, { schemaData });
    }

    return () => {
      try {
        const schemaElement = document.querySelector('#local-business-schema');
        if (schemaElement) {
          schemaElement.remove();
        }
      } catch (error) {
        console.error('LocalBusinessSchema: Error removing schema from DOM', error);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, JSON.stringify(customData)]);

  return null;
}